export interface CandidateItem {
  id: string | number; // config_id / map_id — shown as "(id)" next to the name
  name: string;
  description: string; // shown as the subtitle line
  destination_table?: string | null;
}

// The candidate list for a Create/Edit picker is built entirely from Q1
// (Families Overview) — every config/map currently attached to ANY family
// on this interface+step already carries everything a candidate needs
// (identity, destination_table). This avoids depending on a separate,
// real-shape-unconfirmed catalogue endpoint, and works identically for
// Fetcher/Unifier/Advisor since Q1 covers all three the same way. The one
// tradeoff: a config/map that has never been attached to any family on this
// interface won't appear here, since Q1 only lists things already attached
// somewhere.
export function buildCandidatesFromThings(thingsForStep: any[]): CandidateItem[] {
  const seen = new Map<string, CandidateItem>();
  thingsForStep.forEach((thing: any) => {
    const id = thing.config_id ?? thing.map_id;
    if (id == null) return;
    const key = String(id);
    if (seen.has(key)) return;
    const isFetcherThing = thing.config_id != null;
    seen.set(key, {
      id,
      name: thing.configuration_name ?? thing.map_name,
      description: isFetcherThing
        ? `${thing.configuration_name ?? ""} - ${thing.collection_type ?? ""}`
        : thing.destination_table ?? "",
      destination_table: thing.destination_table ?? null,
    });
  });
  return Array.from(seen.values());
}

// Q2 (Interface Coverage) carries the full config/map catalogue for each
// interface as three JSON-encoded columns, one per step. Unlike Q1 this
// includes configs/maps that aren't attached to any family yet, so it's the
// real source for the Create picker.
export interface FetcherConfigItem {
  id: number;
  configuration_name: string;
}

export interface UnifierMapItem {
  id: number;
  map_name: string;
}

export interface AdvisorMapItem {
  id: number;
  name: string;
}

export interface CoverageRow {
  backend_interface_id: number | null;
  interface_name: string;
  fetcher_configs?: string | FetcherConfigItem[] | null;
  unifier_maps?: string | UnifierMapItem[] | null;
  advisor_maps?: string | AdvisorMapItem[] | null;
  [key: string]: any;
}

export type CoverageStepKey = "fetcher" | "unifier" | "advisor";

// Per step: which Q2 column holds the catalogue, and which field is the name.
const COVERAGE_SOURCE: Record<CoverageStepKey, { column: keyof CoverageRow; nameField: string }> = {
  fetcher: { column: "fetcher_configs", nameField: "configuration_name" },
  unifier: { column: "unifier_maps", nameField: "map_name" },
  advisor: { column: "advisor_maps", nameField: "name" },
};

// Accepts an already-parsed array or a JSON-encoded string; anything else
// (null, "", malformed JSON, a non-array value) falls back to [].
export function parseJsonList<T = any>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function buildCandidatesFromCoverageRow(
  coverageRow: CoverageRow | null | undefined,
  stepKey: CoverageStepKey | undefined,
): CandidateItem[] {
  if (!coverageRow || !stepKey) return [];
  const { column, nameField } = COVERAGE_SOURCE[stepKey];
  const seen = new Map<string, CandidateItem>();
  parseJsonList(coverageRow[column]).forEach((item: any) => {
    if (item?.id == null) return;
    const key = String(item.id);
    if (seen.has(key)) return;
    const name = String(item[nameField] ?? "");
    seen.set(key, { id: item.id, name, description: stepKey === "fetcher" ? name : "" });
  });
  return Array.from(seen.values());
}

// Q2 is the catalogue (the only source of options) but only carries id +
// name; Q1 things carry the destination_table / collection_type the drawer
// needs for its subtitle and destination-dedup rule. Keep Q2's items and
// order, enriching matching ids from Q1 — Q1-only items are not added.
export function enrichCandidates(
  catalogue: CandidateItem[],
  enrichment: CandidateItem[],
): CandidateItem[] {
  const enrichById = new Map(enrichment.map((c) => [String(c.id), c]));
  return catalogue.map((c) => {
    const extra = enrichById.get(String(c.id));
    return extra ? { ...extra, id: c.id, name: c.name || extra.name } : c;
  });
}

// "name (id)", e.g. "admiral_report (163)".
export function formatCandidateLabel(item: CandidateItem): string {
  return `${item.name} (${item.id})`;
}
